import React from 'react';
import {
  View,
  Text,
  Pressable,
  Modal,
  useWindowDimensions,
  type ViewProps,
  type TextProps,
  type PressableProps,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { KEYBOARD_BOTTOM_OFFSET } from '@/components/dashboard-scroll-view';
import { cn } from '@/utils/cn';

export interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  dismissable?: boolean;
}

export function Dialog({ open = false, onOpenChange, children, dismissable = true }: DialogProps) {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  return (
    <Modal
      visible={open}
      transparent
      animationType="fade"
      onRequestClose={() => {
        if (!dismissable) return;
        onOpenChange?.(false);
      }}
    >
      {/* Fundo rolável e ciente do teclado: o cartão fica centralizado; com o
          teclado aberto, o campo focado sobe até ficar visível e, se o cartão
          não couber, dá pra rolar até o resto. Toque em opção/botão com o
          teclado aberto age de primeira. */}
      <KeyboardAwareScrollView
        style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)' }}
        contentContainerStyle={{ flexGrow: 1 }}
        bottomOffset={KEYBOARD_BOTTOM_OFFSET}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <Pressable
          className="flex-1 items-center justify-center"
          style={
            isMobile
              ? {
                  paddingTop: insets.top,
                  paddingBottom: insets.bottom,
                  paddingHorizontal: 12,
                }
              : undefined
          }
          onPress={() => {
            if (!dismissable) return;
            onOpenChange?.(false);
          }}
        >
          {children}
        </Pressable>
      </KeyboardAwareScrollView>
    </Modal>
  );
}

export interface DialogContentProps extends ViewProps {
  children: React.ReactNode;
  className?: string;
}

export function DialogContent({ children, className, ...props }: DialogContentProps) {
  return (
    <Pressable
      onPress={(e) => e.stopPropagation()}
      className={cn(
        'w-[90%] max-w-lg rounded-xl border border-border bg-card p-6 text-card-foreground shadow-xl',
        className,
      )}
      {...props}
    >
      {children}
    </Pressable>
  );
}

export interface DialogHeaderProps extends ViewProps {
  children: React.ReactNode;
  className?: string;
}

export function DialogHeader({ children, className, ...props }: DialogHeaderProps) {
  return (
    <View
      className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)}
      {...props}
    >
      {children}
    </View>
  );
}

export interface DialogTitleProps extends TextProps {
  children: React.ReactNode;
  className?: string;
}

export function DialogTitle({ children, className, ...props }: DialogTitleProps) {
  return (
    <Text
      className={cn('font-semibold text-lg leading-none tracking-tight text-foreground', className)}
      {...props}
    >
      {children}
    </Text>
  );
}

export interface DialogDescriptionProps extends TextProps {
  children: React.ReactNode;
  className?: string;
}

export function DialogDescription({ children, className, ...props }: DialogDescriptionProps) {
  return (
    <Text className={cn('text-sm text-foreground/60', className)} {...props}>
      {children}
    </Text>
  );
}

export interface DialogFooterProps extends ViewProps {
  children: React.ReactNode;
  className?: string;
}

export function DialogFooter({ children, className, ...props }: DialogFooterProps) {
  return (
    <View
      className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
      {...props}
    >
      {children}
    </View>
  );
}

export interface DialogCloseProps extends PressableProps {
  children?: React.ReactNode;
  className?: string;
}

export function DialogClose({ children, className, ...props }: DialogCloseProps) {
  return (
    <Pressable
      className={cn('absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100', className)}
      {...props}
    >
      {children || <Text className="text-foreground">✕</Text>}
    </Pressable>
  );
}
